import React, { FC, useEffect } from 'react';
import DocumentTitle from '../components/DocumentTitle';
import { ProductSorting } from '../../../models/Product';
import { getLoanFormat } from '../utils/getLoanFormat';
import { useParams } from 'react-router';
import { useBundleCategories } from '../../api/bundles/useBundleCategories';
import { useBundles } from '../../api/bundles/useBundles';
import { LoadingWrapper } from '../components/LoadingWrapper';
import LoadingOverlay from '../components/LoadingOverlay';
import { ContainerInnerWithColumns } from '../common/ContainerInner';
import { ListCategories } from './ListCategories';
import BundleTileContainer from './BundleTileContainer';
import NoElementsFound, { NO_ELEMENTS_BUNDLES } from '../components/NoElementsFound';
import Pagination from '../common/Pagination';
import { SortByDropDown } from '../common/SortByDropDown';
import { usePreviousProps } from '../../hooks/usePreviousProps';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useAddFromBundleToCart } from '../../api/shopping-cart/useAddFromBundleToCart';
import { useAddFromBundleToSelectionList } from '../../api/selection-list/useAddFromBundleToList';
import { useAgeCategorySearchParam } from '../utils/useAgeCategorySearchParam';
import { SelectionList } from '../../../models/SelectionList';
import { PriceTier } from '../../../models/PriceTier';
import { SubscriptionPeriodType } from '../../../models/Article';
import { ExcludeProductSelection } from '../product-selection/ProductSelectionTypes';
import { MAX_SELECT } from '../product-selection/useProductSelection';
import { TYPE_WARNING } from '../notification/actions';
import { showNotification } from '../notification/toastNotification';
import { usePaginationAndSortParams } from '../hooks/usePaginationAndSortParams';
import { To, useSearchParams } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORTING = [
    { key: 'releaseDateNewToOld', label: 'Release Date: New to Old' },
    { key: 'releaseDateOldToNew', label: 'Release Date: Old to New' },
    { key: 'lastUpdated', label: 'Last updated' },
    { key: 'productCountHighToLow', label: 'Number of Products: High to Low' },
    { key: 'productCountLowToHigh', label: 'Number of Products: Low to High' }
];

export const BundlesOverviewPage: FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const { resetPageParam } = usePaginationAndSortParams();
    const { loanFormat: loanFormatPathParam, shopLanguage } = useParams<'loanFormat' | 'shopLanguage'>();
    const loanFormat = getLoanFormat(loanFormatPathParam);

    const { page, sortBy, pageSize } = usePaginationAndSortParams({
        defaultPageSize: DEFAULT_PAGE_SIZE,
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD
    });

    const categoriesQueryParam = searchParams.get('categories');
    const releaseYear = searchParams.get('releaseYear');

    const { value: ageCategory } = useAgeCategorySearchParam();

    const { data: categories, isLoading: isLoadingCategories } = useBundleCategories({
        ageCategory,
        loanFormat: loanFormat.requestKey!,
        shopLanguage
    });

    const { data: bundlesData, isLoading: isLoadingBundles } = useBundles({
        page,
        pageSize,
        sortBy,
        ageCategory,
        loanFormat: loanFormat.requestKey || undefined,
        category: releaseYear
            ? `releaseYear#${releaseYear}`
            : categoriesQueryParam
            ? `categories#${categoriesQueryParam}`
            : undefined,
        includeProducts: true,
        shopLanguage
    });

    const { addFromBundleToListBulk: addBulkToList } = useAddFromBundleToSelectionList({});
    const { addFromBundleToCartBulk: addBulkToCart } = useAddFromBundleToCart({}, () => {}); // note: we don't have to invalidate the products call, since we don't show a "items in cart" icon on this page

    const bundles = bundlesData?.hits ?? [];
    const totalBundlesCount = bundlesData?.totalHits ?? 0;

    const getBundleProductAmount = (bundleId: string) =>
        bundles.find(bundle => bundle.bundleId === bundleId)?.productAmount ?? 0;

    const onBulkAddToCart = async (
        bundleId: string,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: getBundleProductAmount(bundleId)
        };

        if (productSelection.totalCount > MAX_SELECT) {
            showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING);
            throw new Error('max selection exceeded');
        }

        return addBulkToCart(bundleId, priceTier, subscriptionPeriodType, performChecks, productSelection);
    };

    const onBulkAddToList = (
        bundleId: string,
        list: SelectionList,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: getBundleProductAmount(bundleId)
        };

        if (productSelection.totalCount > MAX_SELECT) {
            showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING);
            throw new Error('max selection exceeded');
        }

        return addBulkToList(bundleId, list.id, priceTier, subscriptionPeriodType, performChecks, productSelection);
    };

    const previousPage = usePreviousProps(page);
    useEffect(() => {
        if (page !== previousPage) {
            window.scrollTo(0, 0);
        }
    }, [page, previousPage]);

    const buildCategoryLinkPath = (item?: { name: string; value: string }): To => {
        let nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams = resetPageParam(nextSearchParams);
        nextSearchParams.delete('releaseYear');
        nextSearchParams.delete('categories');
        if (item) {
            nextSearchParams.set(item.name, item.value);
        }

        return { search: nextSearchParams.toString() };
    };

    const loading = isLoadingBundles || isLoadingCategories;

    return (
        <div data-displayname="ContainerBundle">
            <DocumentTitle title={t('components.documentTitle.curatedContentCollections')}>
                <>
                    {loading ? (
                        <LoadingWrapper>
                            <LoadingOverlay loading={loading} />
                        </LoadingWrapper>
                    ) : null}
                    <ContainerInnerWithColumns>
                        <div className="filter-bar">
                            <div className="layout-column-33 fl-left">
                                <h4>{t('menu.shop.languages.curatedCollections')}</h4>
                            </div>
                            <div className="layout-column-33 fl-right">
                                {bundles.length > 0 ? (
                                    <SortByDropDown sorting={DEFAULT_SORTING} sortBy={sortBy} />
                                ) : null}
                            </div>
                            <div className="layout-column-33 fl-left">
                                {bundles.length > 0 ? (
                                    <Pagination totalItems={totalBundlesCount} pageSize={pageSize} className="clear" />
                                ) : null}
                            </div>
                        </div>
                        {bundles.length > 0 || loading ? (
                            <div className="flex-ca">
                                <ListCategories
                                    categories={categoriesQueryParam}
                                    releaseYear={releaseYear}
                                    bundleCategories={{
                                        releaseYear: categories?.aggregations?.releaseYear,
                                        categories: categories?.aggregations?.categories,
                                        totalCount: categories?.totalHits ?? 0
                                    }}
                                    buildUrlPath={buildCategoryLinkPath}
                                />
                                <div
                                    className={classNames(
                                        { 'min-h-screen': isLoadingBundles },
                                        'inner-content bndl-list'
                                    )}
                                >
                                    {bundles.map(bundle => (
                                        <BundleTileContainer
                                            key={bundle.bundleId}
                                            bundle={bundle}
                                            onAddToList={onBulkAddToList}
                                            onAddToCart={onBulkAddToCart}
                                            shopLanguage={shopLanguage}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <NoElementsFound type={NO_ELEMENTS_BUNDLES} />
                        )}
                        {bundles.length > 0 ? (
                            <Pagination totalItems={totalBundlesCount} pageSize={pageSize} className="clear" />
                        ) : null}
                    </ContainerInnerWithColumns>
                </>
            </DocumentTitle>
        </div>
    );
};
